import { PrimaryAnnotatedType } from '@/components/annotated-type';
import {
  OnStepEnterExitArgs,
  ScrollGraphic,
  ScrollRoot,
  ScrollStep,
  ScrollSteps,
} from '@/components/microstory/scroll';
import { StoryText } from '@/components/microstory/text';
import { StripPlot } from '@/components/strip-plot';
import { countryIdToName } from '@/utils/country-id-to-name';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { t, Trans } from '@lingui/macro';
import { useState } from 'react';

type ChildPowerChapter3Props = {
  data: ChildPowerData['importance_to_listen_to_children_country_bd'];
};

export function ChildPowerChapter3(props: ChildPowerChapter3Props) {
  const { data } = props;

  const formatValue = useNumberFormatting('percent');
  const [highlightedCountries, setHighlightedCountries] = useState<string[]>();

  const handleStepEnter = async (evt: OnStepEnterExitArgs<string[] | undefined>) => {
    setHighlightedCountries(evt.data);
  };

  const chartData = (Object.entries(data) as [CountryIsoCode, number][]).map(([key, value]) => ({
    id: key,
    label: countryIdToName(key),
    value,
  }));

  return (
    <ScrollRoot>
      <ScrollGraphic>
        <StripPlot
          data={chartData}
          getValue={(d) => d.value}
          formatValue={formatValue}
          highlightedIds={highlightedCountries}
          axisLabel={t({
            id: 'child-power.chapter-3.chart-label',
            message:
              '% of 40+ year-olds who say it’s very important that politicians listen to children when making decisions',
          })}
        />
      </ScrollGraphic>

      <ScrollSteps onStepEnter={handleStepEnter}>
        <ScrollStep data={['NG']}>
          <StoryText>
            <Trans id="child-power.chapter-3.text-1">
              Among older people, we see the highest support for politicians listening to children
              in <PrimaryAnnotatedType>Nigeria…</PrimaryAnnotatedType>
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep data={['ZW']}>
          <StoryText>
            <Trans id="child-power.chapter-3.text-2">
              &hellip;and <PrimaryAnnotatedType>Zimbabwe.</PrimaryAnnotatedType> In both these
              countries, half the population are children.
            </Trans>
          </StoryText>
        </ScrollStep>
      </ScrollSteps>
    </ScrollRoot>
  );
}
